import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';

const QuickSearchReviewModal = ({ isOpen, onClose, ingredients: initialIngredients, onConfirm }) => {
    const [items, setItems] = useState([]);
    const [ontologyResults, setOntologyResults] = useState([]);
    const [activeSearchIdx, setActiveSearchIdx] = useState(null);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (isOpen && initialIngredients) {
            setItems(initialIngredients.map(ing => {
                const defaultUnit = ing.unit || ing.canonicalUnit || 'g';
                return {
                    ...ing,
                    selectedId: ing.isAmbiguous ? (ing.candidates[0]?.id || ing.dictionaryId) : ing.dictionaryId,
                    displayAmount: ing.amount || '',
                    displayUnit: defaultUnit,
                    tempName: ing.canonicalName || ing.originalName
                };
            }));
        }
    }, [isOpen, initialIngredients]);

    const handleSearchOntology = async (index, value) => {
        const newItems = [...items];
        newItems[index].tempName = value;
        setItems(newItems);
        setActiveSearchIdx(index);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (!value || value.trim().length < 2) {
            setOntologyResults([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await fetch(`${API_BASE_URL}/api/inventory/search-ontology?q=${encodeURIComponent(value.trim())}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setOntologyResults(data);
                }
            } catch (err) {
                console.error('Ontology search error:', err);
            }
        }, 300);
    };

    const selectFromOntology = (idx, result) => {
        const newItems = [...items];
        newItems[idx].dictionaryId = result.id;
        newItems[idx].selectedId = result.id;
        newItems[idx].canonicalName = result.name;
        newItems[idx].tempName = result.name;
        newItems[idx].isAmbiguous = false;
        newItems[idx].candidates = [];

        if (result.unit) newItems[idx].displayUnit = result.unit;

        setItems(newItems);
        setOntologyResults([]);
        setActiveSearchIdx(null);
    };

    const handleUpdateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;

        // If selecting a new candidate from the family dropdown
        if (field === 'selectedId') {
            const candidate = newItems[index].candidates.find(c => c.id === value);
            if (candidate) {
                newItems[index].dictionaryId = candidate.id;
                newItems[index].canonicalName = candidate.canonicalName;
                newItems[index].tempName = candidate.canonicalName;
                if (candidate.canonicalUnit) newItems[index].displayUnit = candidate.canonicalUnit;
            }
        }

        setItems(newItems);
    };

    const handleConfirm = () => {
        const finalIngredients = items.map(item => ({
            name: item.tempName || item.canonicalName || item.originalName,
            amount: parseFloat(item.displayAmount) || 0,
            unit: item.displayUnit,
            dictionaryId: item.selectedId || item.dictionaryId
        }));
        onConfirm(finalIngredients);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#09090b] rounded-[2rem] max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-white/10 dark:border-white/5 overflow-hidden">

                {/* Header */}
                <div className="p-8 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-slate-50/50 dark:bg-white/5">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary text-2xl font-bold">
                            👨‍🍳
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-gray-800 dark:text-white tracking-tight">Emin Olalım!</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-0.5">Mutfaktakileri doğru anladık mı?</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors bg-white dark:bg-white/5 rounded-xl shadow-sm border border-border-light dark:border-white/10">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content - Scrollable List */}
                <div className="flex-1 overflow-y-auto p-8 space-y-6">
                    {items.map((item, idx) => (
                        <div key={idx} className={`p-6 rounded-2xl border transition-all ${item.isAmbiguous ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30' : 'bg-slate-50/50 dark:bg-white/5 border-gray-100 dark:border-white/5'}`}>
                            <div className="flex flex-col gap-4">

                                {/* Name and Ambiguity Clarification */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Malzeme</label>
                                            {item.dictionaryId && (
                                                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[9px] font-black uppercase tracking-tighter animate-in zoom-in duration-300">
                                                    <span className="material-symbols-outlined text-[10px]">verified</span>
                                                    Sözlükle Bağlandı
                                                </div>
                                            )}
                                        </div>
                                        {item.isAmbiguous && (
                                            <span className="text-[10px] font-bold bg-amber-200 dark:bg-amber-900 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full uppercase tracking-tighter">Netleştirilmeli</span>
                                        )}
                                    </div>

                                    {/* Searchable Name Input */}
                                    <div className="relative">
                                        <div className="relative group">
                                            <input
                                                type="text"
                                                value={item.tempName}
                                                onChange={(e) => handleSearchOntology(idx, e.target.value)}
                                                onFocus={() => setActiveSearchIdx(idx)}
                                                className={`w-full p-4 pr-12 rounded-xl bg-white dark:bg-black border ${item.dictionaryId ? 'border-emerald-500/30' : 'border-gray-200 dark:border-white/10'} text-lg font-black text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                                                placeholder="Malzeme adı ara..."
                                            />
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">
                                                <span className="material-symbols-outlined">{item.dictionaryId ? 'verified' : 'search'}</span>
                                            </div>
                                        </div>

                                        {/* Ontology Search Dropdown */}
                                        {activeSearchIdx === idx && ontologyResults.length > 0 && (
                                            <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl z-[300] max-h-48 overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-2">
                                                {ontologyResults.map((res, rIdx) => (
                                                    <button
                                                        key={rIdx}
                                                        onClick={() => selectFromOntology(idx, res)}
                                                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-white/5 border-b border-gray-50 dark:border-white/5 last:border-0 text-left transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                                                                <span className="material-symbols-outlined text-sm">
                                                                    {res.type === 'FAMILY' ? 'family_history' : 'nutrition'}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-bold text-gray-800 dark:text-white">{res.name}</div>
                                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{res.family}</div>
                                                            </div>
                                                        </div>
                                                        <span className="material-symbols-outlined text-slate-300">add_circle</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Family Dropdown (if ambiguous) */}
                                    {item.isAmbiguous && (
                                        <div className="relative">
                                            <div className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase mb-1.5 ml-1 flex items-center gap-1">
                                                <span className="material-symbols-outlined text-xs">auto_fix_high</span>
                                                Önerilen Seçenekler
                                            </div>
                                            <div className="relative">
                                                <select
                                                    value={item.selectedId}
                                                    onChange={(e) => handleUpdateItem(idx, 'selectedId', e.target.value)}
                                                    className="w-full p-4 rounded-xl bg-white dark:bg-black border border-amber-200 dark:border-amber-900/50 text-base font-bold text-gray-800 dark:text-white appearance-none cursor-pointer focus:ring-2 focus:ring-amber-200 dark:focus:ring-amber-900/50 outline-none"
                                                >
                                                    {item.candidates.map(c => (
                                                        <option key={c.id} value={c.id}>{c.canonicalName}</option>
                                                    ))}
                                                </select>
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-amber-600">
                                                    <span className="material-symbols-outlined">expand_more</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Amount and Unit Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Miktar</label>
                                        <input
                                            type="number"
                                            value={item.displayAmount}
                                            onChange={(e) => handleUpdateItem(idx, 'displayAmount', e.target.value)}
                                            placeholder="Miktar"
                                            className="w-full p-4 rounded-xl bg-white dark:bg-black border border-gray-200 dark:border-white/5 text-base font-bold text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary/40 transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Birim</label>
                                        <select
                                            value={item.displayUnit}
                                            onChange={(e) => handleUpdateItem(idx, 'displayUnit', e.target.value)}
                                            className="w-full p-4 rounded-xl bg-white dark:bg-black border border-gray-200 dark:border-white/5 text-base font-bold text-gray-800 dark:text-white outline-none cursor-pointer"
                                        >
                                            {['g', 'ml', 'adet', 'paket', 'su bardağı', 'yemek kaşığı', 'tatlı kaşığı', 'çay kaşığı'].map(u => (
                                                <option key={u} value={u}>{u}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-8 border-t border-gray-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/5 flex gap-4">
                    <button
                        onClick={onClose}
                        className="flex-1 py-4 px-6 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-white/10 transition-all active:scale-95"
                    >
                        Vazgeç
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="flex-[2] py-4 px-6 rounded-2xl bg-primary text-white font-black text-lg shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
                    >
                        Tarifleri Bul 🚀
                    </button>
                </div>

            </div>
        </div>
    );
};

export default QuickSearchReviewModal;
